import React, { useEffect, useState } from "react";
import { Save, ImagePlus } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useSite } from "../../context/AppContext";
import MediaPicker from "../../components/MediaPicker";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-amber-500 bg-white";

const HEADING_FONTS = ["Cormorant Garamond", "Cinzel", "Playfair Display", "Lora"];
const BODY_FONTS = ["Plus Jakarta Sans", "Lato", "Source Sans 3", "Nunito Sans", "Lora"];

function ColorField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border border-slate-200" data-testid={testid} aria-label={label} />
      <span>
        <span className="block text-xs font-semibold text-slate-600">{label}</span>
        <span className="block text-[11px] text-slate-400 font-mono">{value}</span>
      </span>
    </label>
  );
}

export default function AdminBranding() {
  const { site, refreshSite } = useSite();
  const [b, setB] = useState(null);
  const [picker, setPicker] = useState(null); // 'logo' | 'favicon'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (site?.branding) setB(JSON.parse(JSON.stringify(site.branding)));
  }, [site]);

  if (!b) return <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", { branding: b });
      await refreshSite();
      toast.success("Design saved — live on your website");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-branding">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Branding & Design</h2>
          <p className="text-slate-500 text-sm">Colors, fonts, and your logo. Sensible defaults keep text readable on phones.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-branding-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#0B1D3A] text-amber-300 text-sm font-semibold disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Design"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-[#0B1D3A]">Logo & favicon</h3>
            {["logo", "favicon"].map((kind) => (
              <div key={kind} className="flex items-center gap-3">
                <img src={kind === "logo" ? b.logoUrl : b.faviconUrl} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-200" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-slate-600 capitalize">{kind}</p>
                  <button onClick={() => setPicker(kind)} data-testid={`branding-change-${kind}`} className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-lg bg-[#0B1D3A] text-amber-200 text-xs font-semibold">
                    <ImagePlus className="w-3.5 h-3.5" /> Change {kind}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-semibold text-[#0B1D3A] mb-3">Colors</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <ColorField label="Primary (navy)" value={b.primaryColor} onChange={(v) => setB({ ...b, primaryColor: v })} testid="color-primary" />
              <ColorField label="Accent (gold)" value={b.accentColor} onChange={(v) => setB({ ...b, accentColor: v })} testid="color-accent" />
              <ColorField label="Page background" value={b.bgColor} onChange={(v) => setB({ ...b, bgColor: v })} testid="color-bg" />
              <ColorField label="Text" value={b.textColor} onChange={(v) => setB({ ...b, textColor: v })} testid="color-text" />
              <ColorField label="Buttons" value={b.buttonColor} onChange={(v) => setB({ ...b, buttonColor: v })} testid="color-button" />
              <ColorField label="Button text" value={b.buttonTextColor} onChange={(v) => setB({ ...b, buttonTextColor: v })} testid="color-button-text" />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="font-semibold text-[#0B1D3A] mb-3">Social media links <span className="font-normal text-xs text-slate-400">(leave blank to hide)</span></h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {["facebook", "instagram", "tiktok", "youtube"].map((k) => (
                <label key={k} className="block">
                  <span className="block text-xs font-semibold text-slate-600 mb-1 capitalize">{k}</span>
                  <input value={b.socialLinks?.[k] || ""} onChange={(e) => setB({ ...b, socialLinks: { ...b.socialLinks, [k]: e.target.value } })}
                    placeholder={`https://${k}.com/…`} className={inputCls} data-testid={`social-input-${k}`} />
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-[#0B1D3A]">Fonts</h3>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Headings</span>
              <select value={b.headingFont} onChange={(e) => setB({ ...b, headingFont: e.target.value })} className={inputCls} data-testid="font-heading-select">
                {HEADING_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Body text</span>
              <select value={b.bodyFont} onChange={(e) => setB({ ...b, bodyFont: e.target.value })} className={inputCls} data-testid="font-body-select">
                {BODY_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Heading size</span>
              <select value={b.headingScale} onChange={(e) => setB({ ...b, headingScale: e.target.value })} className={inputCls} data-testid="heading-scale-select">
                <option value="small">Smaller</option>
                <option value="medium">Medium (default)</option>
                <option value="large">Larger</option>
              </select>
            </label>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-[#0B1D3A]">Style presets</h3>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Button shape</span>
              <select value={b.buttonStyle} onChange={(e) => setB({ ...b, buttonStyle: e.target.value })} className={inputCls} data-testid="button-style-select">
                <option value="pill">Pill (rounded)</option>
                <option value="rounded">Soft corners</option>
                <option value="sharp">Sharp corners</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Section spacing</span>
              <select value={b.spacingPreset} onChange={(e) => setB({ ...b, spacingPreset: e.target.value })} className={inputCls} data-testid="spacing-preset-select">
                <option value="compact">Compact</option>
                <option value="comfortable">Comfortable (default)</option>
                <option value="spacious">Spacious</option>
              </select>
            </label>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
            Tip: the consultation fee wording lives under <strong>Website Settings</strong>, and page photos can be changed under <strong>Pages & Sections</strong>.
          </div>
        </div>
      </div>

      <MediaPicker open={!!picker} onClose={() => setPicker(null)}
        onSelect={(sel) => setB({ ...b, [picker === "logo" ? "logoUrl" : "faviconUrl"]: sel.url })} />
    </div>
  );
}
